import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
  Label,
} from "recharts";
import { List, ListItem, ListItemText, Typography } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import HelpIcon from "@mui/icons-material/Help";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: "#1c1834",
          padding: "5px",
          border: "1px solid #ddd",
          borderRadius: "16px",
          color: "#fff",
        }}
      >
        <p>{`Year : ${label}`}</p>
        {payload.map((entry, index) => (
          <p key={index}>{`${entry.name} : ${entry.value}%`}</p>
        ))}
      </div>
    );
  }

  return null;
};

const GrowthBarChart = ({ industry, stats }) => {
  const calculateValue = (percentString, multiplier, base) => {
    const percent = parseInt(percentString, 10);
    return base + percent * multiplier;
  };

  const formattedStats = [];
  for (const year in stats["Growth in terms of Adoption"]["Adoption Rate"]) {
    const adoptionRate = calculateValue(
      stats["Growth in terms of Adoption"]["Adoption Rate"][year],
      3,
      12
    );
    const marketPenetrationRate = calculateValue(
      stats["Growth in terms of Adoption"]["Market Penetration Rate"][year],
      5,
      35
    );
    const userBaseGrowth = calculateValue(
      stats["Growth in terms of Adoption"]["User Base Growth"][year],
      2,
      8
    );

    formattedStats.push({
      year: year,
      AdoptionRate: adoptionRate,
      MarketPenetrationRate: marketPenetrationRate,
      UserBaseGrowth: userBaseGrowth,
    });
  }

  const CustomBar = (props) => {
    const { fill, x, y, width, height, radius = 6 } = props;

    return (
      <path
        d={`M${x},${y + radius} 
            a${radius},${radius} 0 0 1 ${radius},-${radius} 
            h${width - 2 * radius} 
            a${radius},${radius} 0 0 1 ${radius},${radius} 
            v${height - radius} 
            h${-width}z`}
        fill={fill}
      />
    );
  };
  return (
    <div style={{ width: "100%", height: 300 }}>
      <Typography variant="h6" gutterBottom color="#fff">
        Growth in terms of Adoption
        <Tooltip
          title={
            <ul style={{ listStyleType: "none", paddingLeft: 0 }}>
              <li>
                <Typography variant="subtitle1">Adoption Rate:</Typography>
                <Typography variant="body2">
                  This represents the percentage of potential users who have
                  adopted the digital service provider's services. It is
                  calculated by dividing the number of users who have adopted
                  the service by the total addressable market size.
                </Typography>
              </li>
              <li>
                <Typography variant="subtitle1">
                  Market Penetration Rate:
                </Typography>
                <Typography variant="body2">
                  This is the percentage of the total addressable market that
                  the digital service provider has captured. It is calculated by
                  dividing the total number of users by the total addressable
                  market size.
                </Typography>
              </li>
              <li>
                <Typography variant="subtitle1">User Base Growth:</Typography>
                <Typography variant="body2">
                  This indicates the percentage increase in the user base of the
                  digital service provider from one year to the next. It is
                  calculated by subtracting the previous year's user base from
                  the current year's user base, dividing the result by the
                  previous year's user base, and then multiplying by 100 to
                  express it as a percentage.
                </Typography>
              </li>
            </ul>
          }
        >
          <HelpIcon
            fontSize="small"
            style={{ marginLeft: 4, verticalAlign: "middle" }}
          />
        </Tooltip>
      </Typography>
      <Typography variant="body1" gutterBottom color={"grey"}>
        <b>Industry</b> - {industry}
      </Typography>
      <ResponsiveContainer>
        <BarChart data={formattedStats}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" />
          <YAxis>
            <Label
              value="Valuation in percentage"
              angle={-90}
              position="insideLeft"
              style={{ textAnchor: "middle" }}
              offset={4}
              dy={10}
            />
          </YAxis>
          <RTooltip content={<CustomTooltip />} />
          <Legend />
          <Bar
            dataKey="AdoptionRate"
            fill="#6B41FC"
            name="Adoption Rate"
            shape={<CustomBar />}
          />
          <Bar
            dataKey="MarketPenetrationRate"
            fill="#4DC369"
            name="Market Penetration Rate"
            shape={<CustomBar />}
          />
          <Bar
            dataKey="UserBaseGrowth"
            fill="#F49958"
            name="User Base Growth"
            shape={<CustomBar />}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default GrowthBarChart;
